import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getUserTimezone } from '@/utils/date-utils';

interface TimezoneContextType {
  timezone: string;
  setTimezone: (timezone: string) => void;
  availableTimezones: string[];
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

interface TimezoneProviderProps {
  children: ReactNode;
}

export function TimezoneProvider({ children }: TimezoneProviderProps) {
  // Get initial timezone from localStorage or use browser's timezone
  const [timezone, setTimezone] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('preferredTimezone') || getUserTimezone();
    }
    return 'UTC'; // Default for SSR
  });

  // All available IANA timezones
  const [availableTimezones, setAvailableTimezones] = useState<string[]>([]);

  // Save timezone preference to localStorage, HTML data attribute, and global variable when it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('preferredTimezone', timezone);
      
      // Set the global timezone variable for non-React components
      window.__CRIMSON_TIMEZONE__ = timezone;
      
      // Update the HTML data attribute for components that can't access context
      if (typeof document !== 'undefined') {
        document.documentElement.dataset.timezone = timezone;
      }
    }
  }, [timezone]);

  // Populate available timezones
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof Intl !== 'undefined') {
      try {
        // This approach gets all available IANA timezones supported by the browser
        // Format a date with each timezone to create the list
        const timezones = Intl.supportedValuesOf('timeZone');
        setAvailableTimezones(timezones as string[]);
      } catch (error) {
        console.error('Failed to get available timezones:', error);
        // Fallback to common timezones if the browser doesn't support Intl.supportedValuesOf
        setAvailableTimezones([
          'UTC',
          'America/New_York',
          'America/Chicago',
          'America/Denver',
          'America/Los_Angeles',
          'Europe/London',
          'Europe/Paris',
          'Europe/Berlin',
          'Asia/Tokyo',
          'Asia/Shanghai',
          'Australia/Sydney',
          'Pacific/Auckland',
        ]);
      }
    }
  }, []);

  const value = {
    timezone,
    setTimezone,
    availableTimezones,
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezone() {
  const context = useContext(TimezoneContext);
  if (context === undefined) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
}

// Safe timezone getter that doesn't use React hooks - can be used in filter functions
export function getTimezone(): string {
  if (typeof window !== 'undefined') {
    // First try to get from global variable (most up-to-date)
    if (window.__CRIMSON_TIMEZONE__) {
      return window.__CRIMSON_TIMEZONE__;
    }
    
    // Then try from localStorage
    const storedTimezone = localStorage.getItem('preferredTimezone');
    if (storedTimezone) {
      return storedTimezone;
    }
    
    // Fallback to browser timezone
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      console.error('Error getting browser timezone:', e);
    }
  }
  
  // Default to UTC
  return 'UTC';
}